/**
 * Контракты публичного REST API `/api/v1` и управления интеграциями
 * (docs/12). Схемы — единственный источник правды по формам данных.
 */

import { z } from 'zod';

import {
  DateOnlySchema,
  IsoDateTimeSchema,
  IssueTypeSchema,
  PrioritySchema,
  ResolutionSchema,
  StatusCategorySchema,
  UuidSchema,
} from './common.js';

/**
 * Префикс публичного API (docs/12 §3): версия входит в путь, ломающие
 * изменения только в `/api/v2`. Константа общая для сервера и клиента, чтобы
 * справка на вкладке «Интеграции» показывала тот же путь, который слушает
 * сервер (`plugins/public-api.ts`).
 */
export const PUBLIC_API_PREFIX = '/api/v1';

/** Скоупы токена (docs/12 §2.2). */
export const ApiScopeSchema = z.enum([
  'issues:read',
  'issues:write',
  'comments:read',
  'comments:write',
  'attachments:read',
  'attachments:write',
  'webhooks:read',
  'webhooks:write',
  'wiki:read',
  'wiki:write',
]);
export type ApiScope = z.infer<typeof ApiScopeSchema>;

/** Пользователь в публичном API: без аватара и служебных флагов. */
export const PublicUserRefSchema = z.object({
  id: UuidSchema,
  login: z.string(),
  displayName: z.string(),
});
export type PublicUserRef = z.infer<typeof PublicUserRefSchema>;

export const PublicIssueRefSchema = z.object({
  id: UuidSchema,
  key: z.string(),
  summary: z.string(),
  type: IssueTypeSchema,
});
export type PublicIssueRef = z.infer<typeof PublicIssueRefSchema>;

export const PublicStatusSchema = z.object({
  id: UuidSchema,
  name: z.string(),
  category: StatusCategorySchema,
});
export type PublicStatus = z.infer<typeof PublicStatusSchema>;

export const PublicSprintRefSchema = z.object({
  id: UuidSchema,
  name: z.string(),
  state: z.enum(['future', 'active', 'closed']),
});
export type PublicSprintRef = z.infer<typeof PublicSprintRefSchema>;

/** Ссылка на компонент в задаче: id для записи, имя для чтения (docs/13 §5). */
export const PublicComponentRefSchema = z.object({
  id: UuidSchema,
  name: z.string(),
});
export type PublicComponentRef = z.infer<typeof PublicComponentRefSchema>;

/**
 * Элемент справочника `/api/v1/components`: внешней системе нужен `id`,
 * чтобы проставлять компоненты в задачах по id (docs/13 §5).
 */
export const PublicComponentSchema = z.object({
  id: UuidSchema,
  name: z.string(),
  description: z.string(),
  lead: PublicUserRefSchema.nullable(),
  defaultAssignee: PublicUserRefSchema.nullable(),
  archived: z.boolean(),
});
export type PublicComponent = z.infer<typeof PublicComponentSchema>;

/** Элемент списка задач и тело вебхук-события (docs/12 §6.9). */
export const PublicIssueSummarySchema = z.object({
  id: UuidSchema,
  key: z.string(),
  type: IssueTypeSchema,
  summary: z.string(),
  status: PublicStatusSchema,
  priority: PrioritySchema,
  resolution: ResolutionSchema.nullable(),
  assignee: PublicUserRefSchema.nullable(),
  reporter: PublicUserRefSchema,
  parent: PublicIssueRefSchema.nullable(),
  epic: PublicIssueRefSchema.nullable(),
  storyPoints: z.number().nullable(),
  dueDate: DateOnlySchema.nullable(),
  sprint: PublicSprintRefSchema.nullable(),
  labels: z.array(z.string()),
  components: z.array(PublicComponentRefSchema),
  subtasks: z.object({ total: z.number().int(), done: z.number().int() }),
  hasAttachments: z.boolean(),
  commentsCount: z.number().int(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type PublicIssueSummary = z.infer<typeof PublicIssueSummarySchema>;

/** Полная задача: описание и состав подзадач (docs/12 §6.9). */
export const PublicIssueSchema = PublicIssueSummarySchema.extend({
  description: z.string(),
  subtasks: z.object({
    total: z.number().int(),
    done: z.number().int(),
    items: z.array(PublicIssueRefSchema),
  }),
  originalEstimate: z.number().int().nullable(),
  timeSpent: z.number().int(),
  archivedAt: IsoDateTimeSchema.nullable(),
});
export type PublicIssue = z.infer<typeof PublicIssueSchema>;

export const PublicCommentSchema = z.object({
  id: UuidSchema,
  issueId: UuidSchema,
  author: PublicUserRefSchema,
  parentId: UuidSchema.nullable(),
  body: z.string(),
  mentions: z.array(PublicUserRefSchema),
  editedAt: IsoDateTimeSchema.nullable(),
  createdAt: IsoDateTimeSchema,
});
export type PublicComment = z.infer<typeof PublicCommentSchema>;

export const PublicAttachmentSchema = z.object({
  id: UuidSchema,
  issueId: UuidSchema,
  uploader: PublicUserRefSchema,
  filename: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int(),
  createdAt: IsoDateTimeSchema,
});
export type PublicAttachment = z.infer<typeof PublicAttachmentSchema>;

export const PublicChangelogEntrySchema = z.object({
  id: z.number().int(),
  field: z.string(),
  oldValue: z.unknown(),
  newValue: z.unknown(),
  actor: PublicUserRefSchema.nullable(),
  source: z.enum(['ui', 'api', 'system']),
  createdAt: IsoDateTimeSchema,
});
export type PublicChangelogEntry = z.infer<typeof PublicChangelogEntrySchema>;

// --- Служебные ответы ---

export const PublicMeSchema = z.object({
  token: z.object({
    id: UuidSchema,
    name: z.string(),
    prefix: z.string(),
    scopes: z.array(ApiScopeSchema),
    expiresAt: IsoDateTimeSchema.nullable(),
    lastUsedAt: IsoDateTimeSchema.nullable(),
  }),
  owner: PublicUserRefSchema,
  project: z.object({
    id: UuidSchema,
    key: z.string(),
    name: z.string(),
    boardType: z.enum(['kanban', 'scrum']),
  }),
  rateLimit: z.object({ limit: z.number().int(), windowSeconds: z.number().int() }),
});
export type PublicMe = z.infer<typeof PublicMeSchema>;

export const PublicProjectSchema = z.object({
  id: UuidSchema,
  key: z.string(),
  name: z.string(),
  description: z.string(),
  boardType: z.enum(['kanban', 'scrum']),
  isArchived: z.boolean(),
  createdAt: IsoDateTimeSchema,
});
export type PublicProject = z.infer<typeof PublicProjectSchema>;

export const PublicStatusListSchema = z.object({
  items: z.array(
    PublicStatusSchema.extend({
      position: z.number().int(),
      isInitial: z.boolean(),
      wipLimit: z.number().int().nullable(),
      /**
       * Workflow статуса (docs/12 §5.3). Проект может держать несколько
       * флоу; поле добавлено backward-compatible, чтобы интегратор мог
       * сопоставить статус с конкретным набором (docs/03 §1).
       */
      workflowId: UuidSchema,
      /**
       * Ответственный по умолчанию: при переходе в этот статус исполнитель
       * меняется на него; `null` — прежний ответственный сохраняется.
       */
      defaultAssignee: PublicUserRefSchema.nullable(),
    }),
  ),
});
export type PublicStatusList = z.infer<typeof PublicStatusListSchema>;

export const PublicMemberSchema = PublicUserRefSchema.extend({
  role: z.enum(['project_admin', 'member']),
  isActive: z.boolean(),
});
export type PublicMember = z.infer<typeof PublicMemberSchema>;

/** Коллекция с пагинацией (docs/12 §3). */
export const PublicListSchema = z.object({
  total: z.number().int(),
  limit: z.number().int(),
  offset: z.number().int(),
  hasMore: z.boolean(),
});

// --- Запросы к задачам ---

const queryList = (max: number) => z.string().max(max).optional();

export const PublicListIssuesQuerySchema = z.object({
  jql: queryList(2000),
  status: z.union([z.string(), z.array(z.string())]).optional(),
  statusCategory: StatusCategorySchema.optional(),
  type: z.union([IssueTypeSchema, z.array(IssueTypeSchema)]).optional(),
  assignee: z.string().optional(),
  reporter: z.string().optional(),
  label: z.union([z.string(), z.array(z.string())]).optional(),
  /** Повторяемый фильтр по id компонента; `none` — «без компонента» (docs/13 §5). */
  component: z.union([z.string(), z.array(z.string())]).optional(),
  sprintId: z.string().optional(),
  epic: z.string().optional(),
  q: queryList(100),
  updatedSince: IsoDateTimeSchema.optional(),
  updatedBefore: IsoDateTimeSchema.optional(),
  createdSince: IsoDateTimeSchema.optional(),
  createdBefore: IsoDateTimeSchema.optional(),
  includeSubtasks: z.coerce.boolean().default(false),
  includeArchived: z.coerce.boolean().default(false),
  sort: z.string().max(40).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type PublicListIssuesQuery = z.infer<typeof PublicListIssuesQuerySchema>;

export const PublicCreateIssueBodySchema = z
  .object({
    type: IssueTypeSchema.default('task'),
    summary: z.string().min(1, 'Введите заголовок').max(255),
    description: z.string().max(50_000).default(''),
    priority: PrioritySchema.default('medium'),
    assigneeId: UuidSchema.nullable().default(null),
    parentId: UuidSchema.nullable().default(null),
    epicId: UuidSchema.nullable().default(null),
    storyPoints: z.number().min(0).max(100).nullable().default(null),
    dueDate: DateOnlySchema.nullable().default(null),
    sprintId: UuidSchema.nullable().default(null),
    labels: z.array(z.string().max(30)).max(20).default([]),
    /**
     * Компоненты — **по id**, а не по имени, как метки: компонент — управляемая
     * запись, и опечаткой её создать нельзя (docs/13 §5).
     */
    components: z.array(UuidSchema).max(10).default([]),
  })
  .strict();
export type PublicCreateIssueBody = z.infer<typeof PublicCreateIssueBodySchema>;

export const PublicUpdateIssueBodySchema = z
  .object({
    summary: z.string().min(1).max(255).optional(),
    description: z.string().max(50_000).optional(),
    priority: PrioritySchema.optional(),
    assigneeId: UuidSchema.nullable().optional(),
    epicId: UuidSchema.nullable().optional(),
    storyPoints: z.number().min(0).max(100).nullable().optional(),
    dueDate: DateOnlySchema.nullable().optional(),
    sprintId: UuidSchema.nullable().optional(),
    labels: z.array(z.string().max(30)).max(20).optional(),
    /** Полная замена набора компонентов задачи (docs/13 §5). */
    components: z.array(UuidSchema).max(10).optional(),
    resolution: ResolutionSchema.nullable().optional(),
    originalEstimate: z.number().int().min(0).nullable().optional(),
    timeSpent: z.number().int().min(0).optional(),
  })
  .strict();
export type PublicUpdateIssueBody = z.infer<typeof PublicUpdateIssueBodySchema>;

export const PublicDeleteIssueQuerySchema = z.object({
  cascade: z.coerce.boolean().default(true),
  expectedUpdatedAt: IsoDateTimeSchema.optional(),
});

export const PublicTransitionBodySchema = z
  .object({
    transitionId: UuidSchema.optional(),
    toStatusId: UuidSchema.optional(),
    resolution: ResolutionSchema.nullable().default(null),
    comment: z.string().max(10_000).optional(),
  })
  .strict()
  .refine((value) => (value.transitionId === undefined) !== (value.toStatusId === undefined), {
    message: 'Укажите ровно одно из полей: transitionId или toStatusId',
    path: ['transitionId'],
  });
export type PublicTransitionBody = z.infer<typeof PublicTransitionBodySchema>;

export const PublicChangelogQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(50),
  offset: z.coerce.number().int().min(0).default(0),
  field: z.union([z.string(), z.array(z.string())]).optional(),
  since: IsoDateTimeSchema.optional(),
});

export const PublicCommentListQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(50),
  offset: z.coerce.number().int().min(0).default(0),
  since: IsoDateTimeSchema.optional(),
});

export const PublicCreateCommentBodySchema = z
  .object({
    body: z.string().min(1, 'Комментарий не может быть пустым').max(10_000),
    parentId: UuidSchema.nullable().default(null),
  })
  .strict();
export type PublicCreateCommentBody = z.infer<typeof PublicCreateCommentBodySchema>;

// --- Токены (внутренний API UI, docs/12 §Приложение A.1) ---

export const ApiTokenSchema = z.object({
  id: UuidSchema,
  name: z.string(),
  prefix: z.string(),
  scopes: z.array(ApiScopeSchema),
  owner: PublicUserRefSchema.nullable(),
  expiresAt: IsoDateTimeSchema.nullable(),
  revokedAt: IsoDateTimeSchema.nullable(),
  lastUsedAt: IsoDateTimeSchema.nullable(),
  lastUsedIp: z.string().nullable(),
  requestCount: z.number().int(),
  isActive: z.boolean(),
  createdAt: IsoDateTimeSchema,
});
export type ApiToken = z.infer<typeof ApiTokenSchema>;

export const CreateApiTokenBodySchema = z
  .object({
    name: z.string().min(1, 'Введите название').max(100),
    scopes: z.array(ApiScopeSchema).min(1, 'Выберите хотя бы одно право'),
    ownerId: UuidSchema.nullable().optional(),
    expiresInDays: z.number().int().min(1).max(365).nullable().optional(),
  })
  .strict();
export type CreateApiTokenBody = z.infer<typeof CreateApiTokenBodySchema>;

export const CreateApiTokenResponseSchema = z.object({
  token: ApiTokenSchema,
  /** Секрет показывается один раз и восстановлению не подлежит. */
  secret: z.string(),
});
export type CreateApiTokenResponse = z.infer<typeof CreateApiTokenResponseSchema>;

// --- Вебхуки (docs/12 §9.2) ---

export const WebhookEventSchema = z.enum([
  'issue.created',
  'issue.updated',
  'issue.transitioned',
  'issue.deleted',
  'comment.created',
  'attachment.added',
  'attachment.deleted',
  'wiki_page.created',
  'wiki_page.updated',
  'wiki_page.deleted',
  'wiki_page.published',
]);
export type WebhookEventType = z.infer<typeof WebhookEventSchema>;

export const WebhookDeliveryStatusSchema = z.enum(['pending', 'delivered', 'failed', 'dead']);

export const WebhookDeliverySchema = z.object({
  id: UuidSchema,
  eventId: UuidSchema,
  eventType: WebhookEventSchema,
  status: WebhookDeliveryStatusSchema,
  attempts: z.number().int(),
  responseStatus: z.number().int().nullable(),
  durationMs: z.number().int().nullable(),
  createdAt: IsoDateTimeSchema,
  deliveredAt: IsoDateTimeSchema.nullable(),
  nextAttemptAt: IsoDateTimeSchema.nullable(),
  error: z.string().nullable(),
});
export type WebhookDelivery = z.infer<typeof WebhookDeliverySchema>;

export const WebhookSchema = z.object({
  id: UuidSchema,
  url: z.string(),
  events: z.array(WebhookEventSchema),
  description: z.string().nullable(),
  isActive: z.boolean(),
  disabledAt: IsoDateTimeSchema.nullable(),
  disabledReason: z.string().nullable(),
  consecutiveFailures: z.number().int(),
  lastDelivery: WebhookDeliverySchema.nullable(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type Webhook = z.infer<typeof WebhookSchema>;

export const CreateWebhookBodySchema = z
  .object({
    url: z.string().url('Некорректный URL').max(2048),
    events: z.array(WebhookEventSchema).min(1, 'Выберите хотя бы одно событие'),
    description: z.string().max(200).nullable().optional(),
    isActive: z.boolean().optional(),
  })
  .strict();
export type CreateWebhookBody = z.infer<typeof CreateWebhookBodySchema>;

export const UpdateWebhookBodySchema = z
  .object({
    url: z.string().url('Некорректный URL').max(2048).optional(),
    events: z.array(WebhookEventSchema).min(1).optional(),
    description: z.string().max(200).nullable().optional(),
    isActive: z.boolean().optional(),
  })
  .strict();
export type UpdateWebhookBody = z.infer<typeof UpdateWebhookBodySchema>;

export const CreateWebhookResponseSchema = z.object({
  webhook: WebhookSchema,
  /** Секрет подписи: показывается при создании и при ротации. */
  secret: z.string(),
});
export type CreateWebhookResponse = z.infer<typeof CreateWebhookResponseSchema>;

export const WebhookTestBodySchema = z
  .object({ eventType: WebhookEventSchema.default('issue.updated') })
  .strict();

export const WebhookDeliveriesQuerySchema = z.object({
  status: WebhookDeliveryStatusSchema.optional(),
  eventType: WebhookEventSchema.optional(),
  from: IsoDateTimeSchema.optional(),
  to: IsoDateTimeSchema.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type WebhookDeliveriesQuery = z.infer<typeof WebhookDeliveriesQuerySchema>;

/** Отметка источника изменения для changelog (docs/12 §2.1). */
export const ChangelogSourceSchema = z.enum(['ui', 'api', 'system']);
